import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { EllipsisVertical, Users, Plus, ArrowLeft, CheckCircle, UserPlus } from 'lucide-react';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { SettingsCard } from './SettingsCard';
import { useCheckEmail, type CheckEmailResult } from '@/hooks/useCheckEmail';
import { useAccountMessageSettings } from '@/hooks/useAccountMessageSettings';
import { useAuth } from '@/contexts/AuthContext';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import ScrollArea from '../ui/shared/ScrollArea';
import { useTranslation } from "react-i18next";
import {
  COUNTRY_CALLING_CODES,
  DEFAULT_COUNTRY_CALLING_CODE,
  formatStoredPhoneForDisplay,
  getCountryCallingCodeLabel,
  joinPhoneWithCountryCode,
  splitPhoneWithCountryCode,
} from '@/data/countryCallingCodes';

const MAX_NATIONAL_PHONE_DIGITS = 15;

function formatNationalPhoneInput(national: string, countryCode: string): string {
  if (!national) return '';
  if (countryCode !== '55') return national;
  if (national.length <= 2) return `(${national}`;
  if (national.length <= 7) return `(${national.slice(0, 2)}) ${national.slice(2)}`;
  if (national.length <= 10) {
    return `(${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
  }
  return `(${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7, 11)}`;
}

interface AreaPermissions {
  dashboard: boolean;
  contatos: boolean;
  agenda: boolean;
  crm: boolean;
  chat: boolean;
  campanhas: boolean;
  assistentes: boolean;
  relatorios: boolean;
  configuracoes: boolean;
}

interface ContactPermissions {
  can_assign_contacts: boolean;
  view_all_contacts: boolean;
  view_unassigned_contacts: boolean;
}

const TeamTab: React.FC = () => {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const { members, loading, error, createMember, updateMember, deleteMember } = useTeam();
  const { checkEmail, checking } = useCheckEmail();
  const {
    showUserNameInMessages,
    loading: settingsLoading,
    saving: savingSettings,
    updateShowUserName
  } = useAccountMessageSettings();

  // Dialog e step control
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState<'email' | 'details'>('email');

  // User check results
  const [checkResult, setCheckResult] = useState<CheckEmailResult | null>(null);

  // Editing
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Form states
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    countryCode: DEFAULT_COUNTRY_CALLING_CODE,
    is_active: true,
    send_email: true
  });
  const [permissions, setPermissions] = useState<AreaPermissions>({
    dashboard: false,
    contatos: false,
    agenda: false,
    crm: false,
    chat: false,
    campanhas: false,
    assistentes: false,
    relatorios: false,
    configuracoes: false
  });
  const [contactPerms, setContactPerms] = useState<ContactPermissions>({
    can_assign_contacts: false,
    view_all_contacts: false,
    view_unassigned_contacts: false
  });

  const areaLabels: Record<keyof AreaPermissions, string> = {
    dashboard: 'Dashboard',
    contatos: 'Contatos',
    agenda: 'Agenda',
    crm: 'CRM',
    chat: 'Chat',
    campanhas: 'Campanhas',
    assistentes: 'Assistentes',
    relatorios: 'Relatórios',
    configuracoes: 'Configurações'
  };

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 6;
  };

  const handleCheckEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateEmail(formData.email)) {
      setSubmitError('Email inválido');
      return;
    }

    if (!user?.account?.id) {
      setSubmitError('Conta não encontrada');
      return;
    }

    const result = await checkEmail(formData.email, user.account.id);

    if (!result) {
      setSubmitError('Erro ao verificar email. Tente novamente.');
      return;
    }

    if (result.alreadyInAccount) {
      setSubmitError('Este usuário já está cadastrado nesta conta');
      return;
    }

    setCheckResult(result);

    // Se usuário existe, preencher nome
    if (result.exists && result.user?.name) {
      setFormData(prev => ({ ...prev, name: result.user!.name || '' }));
    }

    setStep('details');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!editingMember) {
      // Validar senha apenas para novos usuários
      if (!checkResult?.exists && !validatePassword(formData.password)) {
        setSubmitError('Senha deve ter pelo menos 6 caracteres');
        return;
      }
    }

    const roleData = JSON.stringify(permissions);

    try {
      setSubmitting(true);

      const fullPhone = formData.phone
        ? joinPhoneWithCountryCode(formData.countryCode, formData.phone)
        : null;

      if (editingMember) {
        const viewAll = contactPerms.view_all_contacts;
        const dataToUpdate = {
          name: formData.name || null,
          phone: fullPhone,
          role: roleData,
          is_active: formData.is_active,
          can_assign_contacts: contactPerms.can_assign_contacts,
          view_all_contacts: viewAll,
          view_unassigned_contacts: viewAll ? false : contactPerms.view_unassigned_contacts
        };

        const success = await updateMember(editingMember.id, dataToUpdate);
        if (success) {
          setDialogOpen(false);
          resetForm();
        }
      } else {
        const viewAllNew = contactPerms.view_all_contacts;
        const created = await createMember({
          email: formData.email,
          password: formData.password,
          name: formData.name || null,
          phone: fullPhone,
          role: roleData,
          is_active: formData.is_active,
          send_email: formData.send_email,
          view_all_contacts: viewAllNew,
          can_assign_contacts: contactPerms.can_assign_contacts,
          view_unassigned_contacts: viewAllNew ? false : contactPerms.view_unassigned_contacts
        });
        if (created) {
          setDialogOpen(false);
          resetForm();
        }
      }
    } catch (err: any) {
      setSubmitError(err.message || 'Erro ao salvar membro');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    const split = splitPhoneWithCountryCode(member.phone);
    setFormData({
      email: member.email,
      password: '',
      name: member.name || '',
      phone: split.national,
      countryCode: split.countryCode,
      is_active: member.is_active,
      send_email: false
    });

    try {
      const parsedPermissions = member.role ? JSON.parse(member.role) : {};
      setPermissions({
        dashboard: parsedPermissions.dashboard || false,
        contatos: parsedPermissions.contatos || false,
        agenda: parsedPermissions.agenda || false,
        crm: parsedPermissions.crm || false,
        chat: parsedPermissions.chat || false,
        campanhas: parsedPermissions.campanhas || false,
        assistentes: parsedPermissions.assistentes || false,
        relatorios: parsedPermissions.relatorios || false,
        configuracoes: parsedPermissions.configuracoes || false
      });
    } catch {
      setPermissions({
        dashboard: false,
        contatos: false,
        agenda: false,
        crm: false,
        chat: false,
        campanhas: false,
        assistentes: false,
        relatorios: false,
        configuracoes: false
      });
    }

    setContactPerms({
      can_assign_contacts: member.can_assign_contacts ?? false,
      view_all_contacts: member.view_all_contacts ?? false,
      view_unassigned_contacts: member.view_unassigned_contacts ?? false
    });

    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("team.confirmRemove"))) {
      await deleteMember(id);
    }
  };

  const resetForm = () => {
    setEditingMember(null);
    setSubmitError(null);
    setStep('email');
    setCheckResult(null);
    setFormData({
      email: '',
      password: '',
      name: '',
      phone: '',
      countryCode: DEFAULT_COUNTRY_CALLING_CODE,
      is_active: true,
      send_email: true
    });
    setPermissions({
      dashboard: false,
      contatos: false,
      agenda: false,
      crm: false,
      chat: false,
      campanhas: false,
      assistentes: false,
      relatorios: false,
      configuracoes: false
    });
    setContactPerms({
      can_assign_contacts: false,
      view_all_contacts: false,
      view_unassigned_contacts: false
    });
  };

  const toggleAllPermissions = (checked: boolean) => {
    setPermissions({
      dashboard: checked,
      contatos: checked,
      agenda: checked,
      crm: checked,
      chat: checked,
      campanhas: checked,
      assistentes: checked,
      relatorios: checked,
      configuracoes: checked
    });
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const getPermissionsSummary = (roleString: string | null): string => {
    if (!roleString) return 'Sem permissões';

    try {
      const perms = JSON.parse(roleString);
      const active = Object.entries(perms).filter(([_, value]) => value === true);
      if (active.length === 0) return 'Sem permissões';
      if (active.length === 9) return t('common:shared.allAreasActive');
      return `${active.length} ${active.length === 1 ? 'área' : 'áreas'}`;
    } catch {
      return 'Sem permissões';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("team.title")}</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">{t("team.subtitle")}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-[#64748B] dark:text-[#71717A]">
            <Users className="w-5 h-5" />
            <span className="text-sm">{members.length} {members.length === 1 ? 'membro' : 'membros'}</span>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
                <Plus className="w-4 h-4 mr-2" />
                {t("team.newMember")}
              </Button>
            </DialogTrigger>
            <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[500px]">
              <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
                <DialogHeader>
                  <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
                    {editingMember ? 'Editar Membro' :
                      step === 'email' ? 'Adicionar Membro - Etapa 1 de 2' :
                      'Adicionar Membro - Etapa 2 de 2'}
                  </DialogTitle>
                  {!editingMember && (
                    <p className="text-sm text-[#64748B] dark:text-[#71717A] mt-2">
                      {step === 'email' ? 'Digite o email do membro' :
                        checkResult?.exists ? 'Associar usuário existente' : 'Cadastrar novo membro'}
                    </p>
                  )}
                </DialogHeader>

                {/* STEP 1: Email Verification (only for new members) */}
                {!editingMember && step === 'email' && (
                  <form onSubmit={handleCheckEmail} className="w-full mt-4 gap-4 flex flex-col">
                    {submitError && (
                      <div className="p-3 rounded bg-red-900/20 border border-red-900/50 text-[#DC2626] dark:text-[#F87171] text-sm">
                        {submitError}
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="email">{t("team.memberEmail")}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                        placeholder="email@exemplo.com"
                        required
                        disabled={checking}
                      />
                      <p className="text-xs text-[#64748B] dark:text-[#71717A]">
                        {t("team.checkEmailHint")}
                      </p>
                    </div>

                    <div className="flex gap-2 justify-end mt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={checking}>
                        {t("team.cancel")}
                      </Button>
                      <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]" disabled={checking}>
                        {checking ? 'Verificando...' : 'Próximo'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* STEP 2: Details Form (or Edit Form) */}
                {(editingMember || step === 'details') && (
                  <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                    {(submitError || error) && (
                      <div className="p-3 rounded bg-red-900/20 border border-red-900/50 text-[#DC2626] dark:text-[#F87171] text-sm">
                        {submitError || error}
                      </div>
                    )}

                    {/* User Status Badge (only for new members) */}
                    {!editingMember && checkResult && (
                      <div className={`p-3 rounded border ${
                        checkResult.exists
                          ? 'bg-green-900/20 border-green-900/50 text-green-400'
                          : 'bg-blue-900/20 border-blue-900/50 text-blue-400'
                      }`}>
                        <div className="flex items-center gap-2">
                          {checkResult.exists ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              <span className="font-medium">{t("team.userFound")}</span>
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4" />
                              <span className="font-medium">{t("team.newUser")}</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs mt-1">
                          {checkResult.exists
                            ? 'Este usuário já possui cadastro e será associado à sua equipe.'
                            : 'Um novo usuário será criado e adicionado à sua equipe.'}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="email">{t("team.email")}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={editingMember ? editingMember.email : formData.email}
                        disabled
                        className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-[#F1F5F9] text-sm text-[#94A3B8] cursor-not-allowed dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#71717A]"
                      />
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="name">Nome {!editingMember && '(opcional)'}</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t("team.namePlaceholder")}
                        className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                      />
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="phone">{t("team.phone")}</Label>
                      <div className="flex gap-2 w-full">
                        <Select
                          value={formData.countryCode}
                          onValueChange={(value) =>
                            setFormData((prev) => ({
                              ...prev,
                              countryCode: value,
                              phone:
                                value === '55' ? prev.phone.slice(0, 11) : prev.phone,
                            }))
                          }
                        >
                          <SelectTrigger className="h-11 w-[168px] shrink-0 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-[280px] rounded-xl">
                            <SelectGroup>
                              {COUNTRY_CALLING_CODES.map((country) => (
                                <SelectItem
                                  key={`${country.code}-${country.name}`}
                                  value={country.code}
                                >
                                  <span>
                                    {country.flag} {getCountryCallingCodeLabel(country)}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <Input
                          id="phone"
                          value={formatNationalPhoneInput(formData.phone, formData.countryCode)}
                          onChange={(e) => {
                            const maxDigits =
                              formData.countryCode === '55' ? 11 : MAX_NATIONAL_PHONE_DIGITS;
                            const onlyNumbers = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, maxDigits);
                            setFormData((prev) => ({ ...prev, phone: onlyNumbers }));
                          }}
                          placeholder={
                            formData.countryCode === '55' ? '(11) 99999-9999' : 'Número sem DDI'
                          }
                          className="h-11 min-h-[36px] flex-1 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                          type="tel"
                          inputMode="numeric"
                        />
                      </div>
                      <p className="text-xs text-[#94A3B8] dark:text-[#71717A]">
                        {t("team.phoneHint")}
                      </p>
                    </div>

                    {/* Password field only for new users */}
                    {!editingMember && !checkResult?.exists && (
                      <div className="flex flex-col w-full items-start gap-3">
                        <Label htmlFor="password">{t("team.password")}</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]"
                          placeholder={t("team.passwordPlaceholder")}
                          required
                          minLength={6}
                        />
                        <p className="text-xs text-[#64748B] dark:text-[#71717A]">{t("team.passwordHint")}</p>
                      </div>
                    )}

                    <div className="flex flex-col w-full items-start gap-3 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                      <div className="flex items-center justify-between w-full">
                        <Label>{t("team.permissions")}</Label>
                        <button
                          type="button"
                          onClick={() => toggleAllPermissions(!Object.values(permissions).every(v => v))}
                          className="text-sm text-blue-400 hover:text-blue-300"
                        >
                          {Object.values(permissions).every(v => v) ? t("team.uncheckAll") : t("team.checkAll")}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 w-full">
                        {(Object.keys(permissions) as Array<keyof AreaPermissions>).map((area) => (
                          <div key={area} className="flex items-center gap-2">
                            <Checkbox
                              id={area}
                              checked={permissions[area]}
                              onCheckedChange={(checked) => setPermissions({ ...permissions, [area]: !!checked })}
                            />
                            <Label htmlFor={area} className="cursor-pointer text-sm">
                              {areaLabels[area]}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                      <Checkbox
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: !!checked })}
                      />
                      <Label htmlFor="is_active" className="cursor-pointer">{t("team.activeMember")}</Label>
                    </div>

                    <div className="flex flex-col w-full items-start gap-3 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                      <Label className="text-[#334155] dark:text-[#D4D4D8]">{t("team.assignmentPermissions")}</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="can_assign_contacts"
                          checked={contactPerms.can_assign_contacts}
                          onCheckedChange={(checked) =>
                            setContactPerms((p) => ({ ...p, can_assign_contacts: !!checked }))
                          }
                        />
                        <Label htmlFor="can_assign_contacts" className="cursor-pointer text-sm font-normal">
                          {t("team.canAssign")}
                        </Label>
                      </div>
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label className="text-[#334155] dark:text-[#D4D4D8]">{t("team.viewPermissions")}</Label>
                      <div className="grid grid-cols-2 gap-3 w-full">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="view_all_contacts"
                            checked={contactPerms.view_all_contacts}
                            onCheckedChange={(checked) => {
                              const v = !!checked;
                              setContactPerms((p) => ({
                                ...p,
                                view_all_contacts: v,
                                ...(v ? { view_unassigned_contacts: false } : {})
                              }));
                            }}
                          />
                          <Label htmlFor="view_all_contacts" className="cursor-pointer text-sm font-normal">
                            {t("team.allContacts")}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="view_unassigned_contacts"
                            checked={contactPerms.view_unassigned_contacts}
                            disabled={contactPerms.view_all_contacts}
                            onCheckedChange={(checked) =>
                              setContactPerms((p) => ({ ...p, view_unassigned_contacts: !!checked }))
                            }
                          />
                          <Label
                            htmlFor="view_unassigned_contacts"
                            className={`cursor-pointer text-sm font-normal ${contactPerms.view_all_contacts ? 'text-[#64748B] dark:text-[#71717A]' : ''}`}
                          >
                            {t("team.unassignedContacts")}
                          </Label>
                        </div>
                      </div>
                    </div>

                    {/* Send email checkbox (only for new members) */}
                    {!editingMember && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="send_email"
                          checked={formData.send_email}
                          onCheckedChange={(checked) => setFormData({ ...formData, send_email: !!checked })}
                        />
                        <Label htmlFor="send_email" className="cursor-pointer text-sm">
                          {checkResult?.exists
                            ? 'Enviar email de notificação'
                            : 'Enviar dados de acesso por email'}
                        </Label>
                      </div>
                    )}

                    <div className="flex gap-2 justify-end mt-4">
                      {!editingMember && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setStep('email')}
                          disabled={submitting}
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          {t("team.back")}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setDialogOpen(false)}
                        disabled={submitting}
                      >
                        {t("team.cancel")}
                      </Button>
                      <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]" disabled={submitting || loading}>
                        {submitting ? 'Salvando...' : (editingMember ? 'Salvar' : checkResult?.exists ? 'Associar' : 'Cadastrar')}
                      </Button>
                    </div>
                  </form>
                )}
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <SettingsCard className="p-4 flex items-center justify-between gap-4">
        <div>
          <Label htmlFor="show_user_name_in_messages" className="cursor-pointer text-[#0F172A] dark:text-[#FAFAFA]">
            {t("team.showSenderName")}
          </Label>
          <p className="text-xs text-muted-foreground mt-1 dark:text-[#64748B] dark:text-[#71717A]">
            {t("team.showSenderNameHint")}
          </p>
        </div>
        <Switch
          id="show_user_name_in_messages"
          checked={showUserNameInMessages}
          disabled={settingsLoading || savingSettings}
          onCheckedChange={updateShowUserName}
        />
      </SettingsCard>

      <SettingsCard>
        {loading ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("team.loading")}</div>
        ) : members.length === 0 ? (
          <div className="p-8 text-center text-[#64748B] dark:text-[#71717A]">{t("team.empty")}</div>
        ) : (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]">
            {members.map((member) => (
              <div
                key={member.id}
                className="p-4 flex items-center justify-between hover:bg-[#F8FAFC] transition-colors duration-200 dark:hover:bg-[#3F3F46]/50"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {getInitials(member.name, member.email)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                        {member.name || member.email}
                      </h3>
                      {!member.is_active && (
                        <span className="text-xs px-2 py-1 rounded-full bg-[#FEF2F2] text-[#DC2626] dark:bg-red-500/15 dark:text-[#F87171]">
                          {t("team.inactive")}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#64748B] dark:text-[#71717A]">{member.email}</p>
                    {member.phone && (
                      <p className="text-sm text-[#64748B] dark:text-[#71717A]">
                        {formatStoredPhoneForDisplay(member.phone)}
                      </p>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded bg-[#F1F5F9] dark:bg-[#27272A] text-[#475569] dark:text-[#A1A1AA] inline-block mt-1">
                      {getPermissionsSummary(member.role)}
                    </span>
                  </div>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white w-[34px] h-[34px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]" aria-label={t("team.memberActions")}>
                      <EllipsisVertical size={18} className="text-current" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-white p-1 w-[95px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
                    <button
                      onClick={() => handleEdit(member)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]"
                    >
                      {t("team.edit")}
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
                    >
                      {t("team.remove")}
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default TeamTab;
